import { ApiResponse, DataArray, DataObject, DataPaginate } from "@/types";
import { MemberFormPayload } from "./validation";
import { fetcher, sendData } from "@/services/api/fetcher";
import { useFormMutation } from "@/hooks/useFormMutation";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ElectoralDistricMemberResponse,
  MemberByElectoralDistrictResponse,
  MemberResponse,
  MemberStructureResponse,
  PatchMemberResponse,
  RegencyResponse,
} from "./interface";
import { DetailAnggotaResponse } from "../manage-account/jabatan/interface";

// get
const getMember = async (
  query?: string
): Promise<ApiResponse<DataPaginate<MemberResponse>>> => {
  return await fetcher(`user?${query}`);
};

export const useGetMember = (query?: string) => {
  return useQuery<ApiResponse<DataPaginate<MemberResponse>>, Error>({
    queryKey: ["useGetMember", query],
    queryFn: () => getMember(query),
  });
};

const getMemberStucture = async (
  query?: string
): Promise<ApiResponse<DataObject<MemberStructureResponse>>> => {
  return await fetcher(`user/bagan?${query}`);
};

export const useGetMemberStucture = (query?: string) => {
  return useQuery<ApiResponse<DataObject<MemberStructureResponse>>, Error>({
    queryKey: ["useGetMemberStucture", query],
    queryFn: () => getMemberStucture(query),
  });
};

// get by id
export const getMemberId = async (
  id: number | string
): Promise<ApiResponse<DataObject<DetailAnggotaResponse>>> => {
  return await fetcher(`user/${id}`);
};

export const useGetMemberId = (id: number | string) => {
  return useQuery<ApiResponse<DataObject<DetailAnggotaResponse>>, Error>({
    queryKey: ["useGetMemberId", id],
    queryFn: () => getMemberId(id),
    enabled: !!id,
  });
};

export const getPublicMemberId = async (
  nameSlug: string,
  positionSlug: string
): Promise<ApiResponse<DataObject<DetailAnggotaResponse>>> => {
  return await fetcher(`user/${positionSlug}/${nameSlug}`);
};

export const useGetPublicMemberId = (
  nameSlug: string,
  positionSlug: string
) => {
  return useQuery<ApiResponse<DataObject<DetailAnggotaResponse>>, Error>({
    queryKey: ["useGetPublicMemberId", nameSlug, positionSlug],
    queryFn: () => getPublicMemberId(nameSlug, positionSlug),
    enabled: !!nameSlug && !!positionSlug,
  });
};

// electoral district
export const getElectoralDistrictMember = async (
  electoralDistrictId: number
): Promise<ApiResponse<DataArray<ElectoralDistricMemberResponse>>> => {
  return await fetcher(`user/by-dapil/${electoralDistrictId}`);
};

export const useGetElectoralDistrictMember = (electoralDistrictId: number) => {
  return useQuery<
    ApiResponse<DataArray<ElectoralDistricMemberResponse>>,
    Error
  >({
    queryKey: ["useGetElectoralDistricMember", electoralDistrictId],
    queryFn: () => getElectoralDistrictMember(electoralDistrictId),
  });
};

// detail member by dapil id
export const getMemberByElectoralDistrict = async (
  electoralDistrictId: number | string,
  memberId: number | string
): Promise<ApiResponse<DataObject<MemberByElectoralDistrictResponse>>> => {
  return await fetcher(`user/by-dapil/${electoralDistrictId}/user/${memberId}`);
};

export const UseGetMemberByElectoralDistrict = (
  electoralDistrictId: number | string,
  memberId: number | string
) => {
  return useQuery<
    ApiResponse<DataObject<MemberByElectoralDistrictResponse>>,
    Error
  >({
    queryKey: [
      "UseGetMemberByElectoralDistrict",
      electoralDistrictId,
      memberId,
    ],
    queryFn: () => getMemberByElectoralDistrict(electoralDistrictId, memberId),
  });
};

// post
export const useMember = (method: "POST" | "PUT" = "POST", id?: number) => {
  return useFormMutation<
    ApiResponse<DataObject<MemberFormPayload>>,
    Error,
    MemberFormPayload
  >({
    mutationFn: async (
      data
    ): Promise<ApiResponse<DataObject<MemberFormPayload>>> => {
      const endpoint = id ? `user/${id}` : "user";
      const delay = new Promise((resolve) => setTimeout(resolve, 2000));
      const response: ApiResponse<DataObject<MemberFormPayload>> =
        await sendData(endpoint, data, method);
      await delay;
      return response;
    },
    loadingMessage:
      method === "POST" ? "Menyimpan data..." : "Memperbarui data...",
    successMessage:
      method === "POST"
        ? "Data berhasil ditambahkan"
        : "Data berhasil diperbarui",
  });
};

export const usePatchMemberStatus = (id: string) => {
  return useMutation<ApiResponse<DataObject<PatchMemberResponse>>, Error, any>({
    mutationFn: async (): Promise<
      ApiResponse<DataObject<PatchMemberResponse>>
    > => {
      const endpoint = `user/toggle/${id}`;
      const response: ApiResponse<DataObject<PatchMemberResponse>> =
        await sendData(endpoint, {}, "PATCH");
      return response;
    },
  });
};

export const getMemberFraction = async (
  slug: string
): Promise<ApiResponse<DataArray<FraksiResponse>>> => {
  return await fetcher(`user/by-dapil/${slug}/fraksi`);
};

export const useGetMemberFraction = (slug: string) => {
  return useQuery<ApiResponse<DataArray<FraksiResponse>>, Error>(
    ["useGetMemberFraction", slug],
    () => getMemberFraction(slug)
  );
};

export const getMemberDistricts = async (
  slug: string
): Promise<ApiResponse<DataPaginate<RegencyResponse>>> => {
  return await fetcher(`/master/kecamatan`);
};

export const useGetMemberDistricts = (slug: string) => {
  return useQuery<ApiResponse<DataPaginate<RegencyResponse>>, Error>(
    ["useGetMemberDistricts", slug],
    () => getMemberDistricts(slug)
  );
};
